import CountryService from "./CountryService";

// Course/Institution/Agent `address` fields are stored as `string[]`, where each
// element is a JSON-encoded object with these keys (see amplify/data/resource.ts).
export interface ParsedAddress {
  locationName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  addressLine4: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  lat: number | null;
  lon: number | null;
}

export const parseAddressEntry = (raw: string): ParsedAddress | null => {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      locationName: parsed.locationName ?? null,
      addressLine1: parsed.addressLine1 ?? null,
      addressLine2: parsed.addressLine2 ?? null,
      addressLine3: parsed.addressLine3 ?? null,
      addressLine4: parsed.addressLine4 ?? null,
      city: parsed.city ?? null,
      state: parsed.state ?? null,
      postcode: parsed.postcode ?? null,
      lat: parsed.lat ?? null,
      lon: parsed.lon ?? null,
    };
  } catch {
    return null;
  }
};

export const parseAddressList = (
  addresses: string[] | null | undefined,
): ParsedAddress[] => {
  if (!addresses || addresses.length === 0) return [];
  return addresses
    .map(parseAddressEntry)
    .filter((address): address is ParsedAddress => address !== null);
};

export const formatParsedAddress = (address: ParsedAddress): string => {
  const lines = [
    address.addressLine1,
    address.addressLine2,
    address.addressLine3,
    address.addressLine4,
  ].filter(Boolean);
  const cityStatePostcode = [address.city, address.state, address.postcode]
    .filter(Boolean)
    .join(" ");

  return [address.locationName, ...lines, cityStatePostcode]
    .filter(Boolean)
    .join(", ");
};

// Parses one raw address entry and formats it for display. Falls back to the
// raw string when it isn't valid JSON, so plain-text addresses still render.
export const formatAddressString = (raw: string): string => {
  const parsed = parseAddressEntry(raw);
  return parsed ? formatParsedAddress(parsed) : raw;
};

export interface AddressFormFields {
  street1: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Agent/Institution edit forms only collect street1/street2/city/state/postalCode/country.
// The backend JSON has no `country` key, so it's always blank when loaded from storage.
// Falls back to legacy comma-separated strings for records not yet in JSON format.
export const parseAddressEntryToFormFields = (raw: string): AddressFormFields => {
  const parsed = parseAddressEntry(raw);
  if (parsed) {
    return {
      street1: parsed.addressLine1 || "",
      street2: parsed.addressLine2 || "",
      city: parsed.city || "",
      state: parsed.state || "",
      postalCode: parsed.postcode || "",
      country: "",
    };
  }

  const [
    street1 = "",
    street2 = "",
    city = "",
    state = "",
    postalCode = "",
    country = "",
  ] = raw.split(",").map((part) => part.trim());

  return { street1, street2, city, state, postalCode, country };
};

class AddressService {
  constructor(private country: CountryService = CountryService.getInstance()) {}

  public getStateLabel(countryName: string): string {
    const countryData = this.country.getByName(countryName);
    return countryData?.stateLabel || "State/Province/Region";
  }

  public getPostalLabel(countryName: string): string {
    const countryData = this.country.getByName(countryName);
    return countryData?.postalLabel || "Postal Code";
  }

  public shouldShowState(countryName: string): boolean {
    const countryData = this.country.getByName(countryName);
    return countryData?.hasState !== false;
  }

  public isPostalBeforeCity(countryName: string): boolean {
    const countryData = this.country.getByName(countryName);
    return countryData?.postalBeforeCity !== false;
  }
}

export default AddressService;
